import React, { useEffect } from 'react';
import NavBar from './NavBar';
import './BlogArticle.css';

function BlogArticle1() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="blog-article-page">
      <NavBar />

      <article className="article-content">
        <header className="article-header">
          <h1>Understanding Function Calling in LLMs</h1>
          <p className="article-date">January 15, 2024 • 8 min read</p>
        </header>

        <div className="article-body">
          <p>
            Large language models are exceptional at understanding and generating text, but their capabilities
            were traditionally limited to the realm of language. Function calling, also known as tool use,
            fundamentally changes this by allowing LLMs to interact with external systems, APIs, and custom code.
          </p>

          <h2>The Problem: LLMs Can't Do Math</h2>

          <p>
            Let's start with a simple example that illustrates why function calling matters. Try asking
            an LLM to divide two large numbers:
          </p>

          <div className="code-block">
            <pre>User: What is 847392847 divided by 23847?</pre>
          </div>

          <p>
            The model might give you an approximate answer, but it won't be precise. This isn't because the model
            is "bad at math"—it's because LLMs are fundamentally text prediction engines. They can recognize
            patterns and approximate calculations based on training data, but they can't perform exact arithmetic
            operations the way a calculator can.
          </p>

          <h2>The Solution: Give the LLM a Calculator</h2>

          <p>
            Function calling allows us to extend the model's capabilities by giving it access to tools.
            Let's create a simple division tool using Orchestral:
          </p>

          <div className="code-block">
            <pre>{`from orchestral import Agent
from orchestral import define_tool

@define_tool()
def divide_tool(a: float, b: float = 1.0):
    """Divide two numbers a and b
    Returns a / b
    """
    return a / b

agent = Agent(tools=[divide_tool])`}</pre>
          </div>

          <p>
            Now when we ask the agent to divide numbers, it can recognize that it needs to use the
            <code>divide_tool</code> and call it with the appropriate arguments:
          </p>

          <div className="code-block">
            <pre>{`response = agent.run("What is 847392847 divided by 23847?")
# The agent will call divide_tool(847392847, 23847)
# Returns: 35543.67...`}</pre>
          </div>

          <h2>How Function Calling Works</h2>

          <div className="article-image">
            <img src="/function_calling_diagram.png" alt="Function calling workflow diagram" />
            <p className="image-caption">The function calling workflow. Diagram courtesy of OpenAI.</p>
          </div>

          <p>
            The diagram above illustrates the complete workflow:
          </p>

          <ol>
            <li>
              <strong>Tool Definitions + Messages:</strong> The developer provides both the available
              tool definitions (including function signatures and descriptions) and the user's message
              to the model.
            </li>
            <li>
              <strong>Tool Calls:</strong> The model analyzes the request and determines which tools
              to call and with what arguments. It returns structured data specifying the function calls.
            </li>
            <li>
              <strong>Execute Function Code:</strong> The developer's code executes the actual functions
              with the model-provided arguments and collects the results.
            </li>
            <li>
              <strong>Results:</strong> The function results are sent back to the model along with all
              prior messages for context.
            </li>
            <li>
              <strong>Final Response:</strong> The model synthesizes the tool results into a natural
              language response for the user.
            </li>
          </ol>

          <h2>Why This Matters</h2>

          <p>
            Function calling transforms LLMs from pure text generators into agents that can:
          </p>

          <ul>
            <li>Perform precise calculations and data processing</li>
            <li>Query databases and search engines</li>
            <li>Interact with APIs and web services</li>
            <li>Control external systems and automation</li>
            <li>Access real-time information beyond their training data</li>
          </ul>

          <h2>The Orchestral Advantage</h2>

          <p>
            While function calling is powerful, implementing it traditionally requires significant boilerplate:
            defining JSON schemas, parsing function calls, handling errors, managing conversation state, and
            dealing with provider-specific formats.
          </p>

          <p>
            Orchestral abstracts all of this away. You simply write a Python function with type hints and
            a docstring, and the framework handles:
          </p>

          <ul>
            <li>Automatic JSON schema generation from your function signature</li>
            <li>Parameter validation using type hints</li>
            <li>Multi-turn conversation flow (the model can call multiple tools sequentially)</li>
            <li>Provider-agnostic tool definitions (works with OpenAI, Anthropic, Google, etc.)</li>
            <li>Error handling and retry logic</li>
          </ul>

          <h2>Conclusion</h2>

          <p>
            Function calling represents a fundamental shift in how we think about LLMs. Rather than being
            limited to text generation, models become orchestrators that can leverage tools to accomplish
            complex tasks. By combining the reasoning capabilities of language models with the precision
            and power of custom code, we unlock entirely new categories of applications.
          </p>

          <p>
            The example of dividing numbers is simple, but the pattern generalizes to any capability you
            want to give your agent: web scraping, data analysis, controlling robots, managing infrastructure,
            or building complex workflows. Function calling is the bridge between language understanding and
            real-world action.
          </p>
        </div>
      </article>
    </div>
  );
}

export default BlogArticle1;
